import React from "react";
import {
  Badge,
  Button,
  Input,
  PageTitle,
  PageWrapper,
  Section,
  Spinner,
  Table,
} from "@cinesys/ui/components";
import { RotateCcw, Search } from "react-feather";
import { useMutation, useQuery } from "react-query";
import ApiService from "../../services/ApiService";
import { styled } from "styled-components";
import ComponentTypeBadge from "./ComponentTypeBadge";
import { isUserVisibleComponent } from "./utils";
import { useUpdateComponentsJob } from "../../context/UpdateComponentsJobContext";
import Fuse from "fuse.js";

interface PluginConfigItemExtra extends PluginConfigItem {
  organizations: string[];
}

interface RowProps {
  value: PluginConfigItemExtra;
}

const Flex = styled.div`
  display: flex;
  align-items: center;

  > * + * {
    margin-left: 10px;
  }
`;
// Tag bubbles for the organizations a plugin is instanced on — flow-wraps instead of a
// fixed-height scroll box so a long list is all visible (scanning "which orgs" at a glance
// matters more here than keeping row heights uniform).
const Instances = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;
const InstanceBadge = styled(Badge)`
  flex-shrink: 0;
`;
const Buttons = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 10px;
`;
const ListRow = (props: RowProps) => {
  const { value } = props;
  const { addComponentsJob } = useUpdateComponentsJob();

  const updateOrganizationsMutation = useMutation({
    mutationFn: () =>
      ApiService.fetch<{ scriptExecutionId: string; systemDomainId: string }>(
        "POST",
        `/api/config/plugin/${value.id}/update_organizations`
      ),
    onSuccess: (data) =>
      addComponentsJob(
        data.scriptExecutionId,
        data.systemDomainId,
        "Updating components"
      ),
  });

  return (
    <>
      <Flex>
        <span>{value.title}</span>
        {value.development && (
          <Badge color="var(--orange100)" label="Development" />
        )}
      </Flex>
      <span>
        {value.components
          .filter(({ type }) => isUserVisibleComponent(type))
          .map(({ type }, index) => (
            <ComponentTypeBadge type={type} key={index} />
          ))}
      </span>
      <Instances>
        {value.organizations.map((organization, index) => (
          <InstanceBadge
            key={`${organization}-${index}`}
            color="var(--blue300)"
            label={organization}
          />
        ))}
      </Instances>
      <Buttons>
        <Button
          label="Update Organizations"
          icon={RotateCcw}
          type="default"
          onClick={() => updateOrganizationsMutation.mutate()}
          loading={updateOrganizationsMutation.isLoading}
          borderless
        />
      </Buttons>
    </>
  );
};

const columns = [
  { title: "Name", width: "1fr" },
  { title: "Components", width: "1fr" },
  { title: "Instances", width: "1fr" },
  { width: "200px" },
];

const ConfigurePluginScriptListScreen = () => {
  const { data, isLoading, isError } = useQuery({
    queryKey: ["configList", "plugin"],
    queryFn: async () => {
      const response = await ApiService.fetch<{
        data: PluginConfigItemExtra[];
      }>("GET", `/api/config/plugin`);
      return response.data;
    },
  });
  const [search, setSearch] = React.useState("");

  let rows = data ?? [];
  if (search) {
    const fuse = new Fuse<PluginConfigItemExtra>(rows ?? [], {
      keys: ["title", "organizations"],
      ignoreLocation: true,
      threshold: 0.2,
      shouldSort: false,
    });

    rows = fuse.search(search).map((result) => result.item);
  }

  return (
    <div>
      <PageTitle
        title="Configured Plugins"
        error={isError ? "Problem loading plugins" : undefined}
      >
        <Input
          value={search}
          onChange={setSearch}
          placeholder="Search"
          icon={Search}
          clearable
        />
      </PageTitle>

      {isLoading ? (
        <Spinner />
      ) : (
        !isError && (
          <PageWrapper>
            <Section>
              <Table
                style={
                  {
                    "--tableHeaderBg": "transparent",
                    "--tableHeaderBorderBottom": "1px solid var(--dark300)",
                    "--tableHeaderShadow": "none",
                  } as React.CSSProperties
                }
                columnGrid
                columns={columns}
                rows={
                  rows.map((value) => ({
                    id: value.id,
                    element: () => <ListRow value={value} />,
                  })) ?? []
                }
                autoHeight={{}}
              />
            </Section>
          </PageWrapper>
        )
      )}
    </div>
  );
};

export default ConfigurePluginScriptListScreen;
